<template>
    <div class='container'>
        <div class="top">
            <el-radio-group v-model="scope">
                <el-radio-button :label="1">日汇总</el-radio-button>
                <el-radio-button :label="2">月汇总</el-radio-button>
            </el-radio-group>
            <span class="date-title">日期选择：</span>
            <el-date-picker
                v-model="daterange"
                :type="dateRangeType"
                size="small"
                align="right"
                unlink-panels
                range-separator="至"
                value-format="yyyy-MM-dd"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                @change="datePickerChanged"
                :picker-options="pickerOptions">
            </el-date-picker>
        </div>
        <el-table v-loading="isLoading"
            ref="elTable"
            style="width: 100%"
            :data="tableData"
            :height="'calc(100vh - 195px)'"
            :empty-text="isLoading ? ' ' : '暂无数据'">
            <el-table-column
                type="index"
                min-width="50">
            </el-table-column>
            <el-table-column
                prop="date"
                label="时间"
                min-width="110">
                <template slot-scope="{ row }">
                    <p>{{ row.date && (scope == 1 ? row.date : row.date.split('-').slice(0, -1).join('-')) }}</p>
                </template>
            </el-table-column>
            <el-table-column
                prop="sales_income"
                label="本店收入"
                align="right"
                min-width="130">
                <template v-slot:header="{ row }">
                    <span>本店收入(元)</span>
                    <question-icon content="本店收入为商品销售收入，不包含会员充值收款"></question-icon>
                </template>
                <template slot-scope="scope">
                    <span style="padding-right: 24px" v-numberAlign>{{ scope.row.sales_income }}</span>
                </template>
            </el-table-column>
            <el-table-column
                prop="order_count"
                align="right"
                min-width="110"
                label="商品销售笔数">
            </el-table-column>
            <el-table-column
                prop="address"
                align="right"
                min-width="90"
                label="客单价(元)">
                <template slot-scope="scope">
                    <span v-numberAlign>{{scope.row.order_count > 0 ? (scope.row.sales_income / scope.row.order_count).toFixed(2) : '-'}}</span>
                </template>
            </el-table-column>
            <el-table-column
                prop="refund_amount"
                align="right"
                min-width="110"
                label="退款金额(元)">
                <template slot-scope="{ row }">
                    <span v-numberAlign>{{ row.refund_amount }}</span>
                </template>
            </el-table-column>
            <el-table-column
                prop="refund_order_count"
                min-width="80"
                align="right"
                label="退款笔数">
            </el-table-column>
            <el-table-column
                prop="recharge_amount"
                align="right"
                min-width="110"
                label="充值金额(元)">
                <template slot-scope="{ row }">
                    <span>{{ row.recharge_amount }}</span>
                </template>
            </el-table-column>
            <el-table-column
                prop="recharge_count"
                align="right"
                min-width="80"
                label="充值笔数">
            </el-table-column>
            <el-table-column
                prop="consume_amount"
                align="right"
                min-width="110"
                label="余额消费(元)">
                <template slot-scope="{ row }">
                    <span v-numberAlign>{{ row.consume_amount }}</span>
                </template>
            </el-table-column>
            <el-table-column min-width="10"/>
            <el-table-column
                align="right"
                width="120"
                label="总收款(元)">
                <template v-slot:header="{ row }">
                    <span>总收款(元)</span>
                    <question-icon content="总收款=本店收入+充值金额-余额消费金额"></question-icon>
                </template>
                <template slot-scope="{ row }">
                    <span style="padding-right: 24px" v-numberAlign>{{ getSum(row.sales_income, row.recharge_amount, -row.consume_amount) }}</span>
                </template>
            </el-table-column>
            <p v-if="tableData.length > 0 && !isLoading" slot="append" style="text-align: center; padding: 10px;">没有更多了</p>
        </el-table>
    </div>
</template>

<script lang='ts'>
import { createComponent, ref, Ref, onBeforeMount, watch, computed, onMounted } from '@vue/composition-api'
import { $fetch } from '@/api'
import { openMessage, useScrollLoad } from '@/utils/system.tsx'
import { getOneMonthValue } from '@/utils/index'
import dayJs from 'dayjs'
import QuestionIcon from '@/components/question_icon/question_icon.vue'
import { setObjectItemToFixed, getSum } from '@/utils/index'

export default createComponent({
    data() {
        return {
            pickerOptions: {
                shortcuts: [{
                    text: '最近一周',
                    onClick(picker) {
                    const end = new Date()
                    const start = new Date()
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
                    picker.$emit('pick', [start, end])
                    }
                }, {
                    text: '最近一个月',
                    onClick(picker) {
                    const end = new Date()
                    const start = new Date()
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
                    picker.$emit('pick', [start, end])
                    }
                }, {
                    text: '最近三个月',
                    onClick(picker) {
                    const end = new Date()
                    const start = new Date()
                    start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
                    picker.$emit('pick', [start, end])
                    }
                }]
            },
        }
    },
    setup(props, ctx) {
        const scope = ref(1)
        const daterange: Ref<any> = ref(getOneMonthValue())
        const tableData = ref([])
        const isLoading = ref(false)

        let changedOne = false
        let changedTwo = false
        let isFirst = true
        // const { bindListener, isLoading } = useScrollLoad()

        const dateRangeType = computed(() => {
            return +scope.value === 1 ? 'daterange' : 'monthrange'
        })

        onBeforeMount(() => {
            getTableData()
                .then(() => isFirst = false)
        })

        watch([scope], () => {
            if (isFirst) {
                return
            }
            if (+scope.value === 1 && !changedOne) {
                const oneMonth = getOneMonthValue()
                daterange.value = [oneMonth[0], oneMonth[1]]
            }
            if (+scope.value === 2 && !changedTwo) {
                daterange.value = (getOneMonthValue(true) as any[]).map((val, i) => {
                    if (i === 0) {
                        return dayJs(val).date(1).add(1, 'month').toDate()
                    } else {
                        return dayJs(val).date(1).toDate()
                    }
                })
            }
            getTableData()
        })

        function getTableData(page = 0) {
            let dateParams = {}
            const oneMonthVal = getOneMonthValue(true)
            if (+scope.value === 1) {
                if (changedOne) {
                    dateParams = {
                        from_date: dayJs((daterange as any).value[0]).format('YYYY-MM-DD'),
                        to_date: dayJs((daterange as any).value[1]).format('YYYY-MM-DD')
                    }
                } else {
                    dateParams = {
                        from_date: oneMonthVal[0],
                        to_date: oneMonthVal[1]
                    }
                }
            } else {
                if (changedTwo) {
                    dateParams = {
                        from_date: dayJs(daterange.value[0] as any).date(1).format('YYYY-MM-DD'),
                        before_date: dayJs(daterange.value[1] as any).date(1).add(1, 'month').format('YYYY-MM-DD')
                    }
                } else {
                    dateParams = {
                        from_date: dayJs(oneMonthVal[0]).date(1).add(1, 'month').format('YYYY-MM-DD'),
                        before_date: dayJs(oneMonthVal[1]).date(1).add(1, 'month').format('YYYY-MM-DD')
                    }
                }
            }
            isLoading.value = true
            return $fetch.post({
                url: "/finance/getfinanceoverview",
                params: {
                    scope: scope.value,
                    ...dateParams
                }
            }).then(data => {
                tableData.value = setObjectItemToFixed(data)
                return data
            }).catch(e => {
                openMessage("error", e.message)
            }).finally(() => {
                isLoading.value = false
            })
        }

        const datePickerChanged = () => {
            if (+scope.value === 1) {
                !changedOne && (changedOne = true)
            } else {
                !changedTwo && (changedTwo = true)
            }
            getTableData()
        }

        return {
            scope,
            daterange,
            tableData,
            dateRangeType,
            isLoading,
            datePickerChanged,
            getSum
        }
    },
    components: {
        QuestionIcon
    }
})
</script>

<style lang="scss" scoped>
.container {

    .top {
        margin-top: 12px;

        .date-title {
            margin-left: 20px;
            color: #333333;
            font-size: 14px;
        }

        ::v-deep .el-date-editor {
            display: inline-flex;
            align-items: center;
            width: 236px !important;
            height: 36px;
            margin-right: 12px;
            line-height: 1;

            &.is-clearable {
                width: 252px !important;

                .el-range__close-icon {
                    display: inline-block;
                }
            }

            > * {
                height: auto;
            }

            .el-range-input,
            .el-range-separator {
                font-size: 14px;
            }

            .el-range-separator {
                width: 20px;
                padding: 0 !important;
                margin: 0 !important;
                transform: translateY(1px);
                font-size: 14px;
            }

            .el-range-input::placeholder {
                color: #999;
            }

            .el-range__close-icon {
                display: none;
            }

            .el-input__icon::before {
                font-size: 16px;
            }
        }
    }
}
</style>

